/**
 * DashboardPage - Command Centre
 *
 * Modern SaaS dashboard matching the reference design:
 * - Stats Cards Row: Active Sessions, Infrastructure, Overdue, Stunned
 *   - With trend indicators and larger icons
 * - Radio Fleet Table: Filterable table with status indicators
 *   - Filter pills inline with search
 *   - Sortable columns with indicators
 *   - Radio icons in ID cells
 *   - Lock icon for stunned filter
 * - Fleet Overview Panel: Status metrics with activity chart
 *   - Coloured status card (amber for Degraded, green for Healthy)
 *   - Time labels on activity chart
 *   - Horizontal legend with counts
 *
 * Design System: Clean, minimal SaaS aesthetic
 * Typography: Inter font family
 * Theme: Light/dark mode support
 */

import { Box, Group, SimpleGrid, Stack, Text, UnstyledButton, TextInput, Table, ScrollArea } from '@mantine/core';
import { useActiveSessions, useTopology } from '@commander/api-client';
import React, { ReactNode, useState, useMemo, useEffect, useRef } from 'react';

/* ====================================
   Design Tokens
   ==================================== */
const colours = {
  // Accent colours (consistent across themes)
  blue: '#3B82F6',
  orange: '#F59E0B',
  red: '#EF4444',
  green: '#22C55E',
  amber: '#F59E0B',
  grey: '#94A3B8',
};

/* ====================================
   CSS Keyframe Animations (injected)
   ==================================== */
const keyframes = `
  @keyframes slideInUp {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes livePulse {
    0%, 100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.6;
      transform: scale(0.95);
    }
  }

  @keyframes progressFill {
    from {
      width: 0;
    }
  }

  /* Premium: staggered table row entrance animation */
  @keyframes tableRowSlideIn {
    from {
      opacity: 0;
      transform: translateY(6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* Premium: subtle shimmer effect for hover on status box */
  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }

  /* Premium: bar chart grow-in animation */
  @keyframes barGrowIn {
    from {
      transform: scaleY(0);
    }
    to {
      transform: scaleY(1);
    }
  }

  /* Premium: pulse ring effect for the Live indicator */
  @keyframes pulseRing {
    0% {
      transform: scale(1);
      opacity: 0.6;
    }
    70% {
      transform: scale(2.2);
      opacity: 0;
    }
    100% {
      transform: scale(2.2);
      opacity: 0;
    }
  }

  /* Premium: core dot gentle pulse for the Live indicator */
  @keyframes liveDotPulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.7;
    }
  }
`;

// Inject keyframes into document head (only once)
if (typeof document !== 'undefined') {
  const styleId = 'dashboard-keyframes';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = keyframes;
    document.head.appendChild(style);
  }
}

/* Mock data has been removed - dashboard now uses real API data only */

/* ====================================
   Icon Components
   ==================================== */
function IconUsers({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function IconBuilding({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
      <path d="M9 22v-4h6v4" />
      <path d="M8 6h.01" />
      <path d="M16 6h.01" />
      <path d="M12 6h.01" />
      <path d="M12 10h.01" />
      <path d="M12 14h.01" />
      <path d="M16 10h.01" />
      <path d="M16 14h.01" />
      <path d="M8 10h.01" />
      <path d="M8 14h.01" />
    </svg>
  );
}

function IconAlertTriangle({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function IconLock({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function IconSearch({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function IconRadio({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="2" />
      <path d="M16.24 7.76a6 6 0 0 1 0 8.49m-8.48-.01a6 6 0 0 1 0-8.49" />
    </svg>
  );
}

function IconMapPin({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function IconUser({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function IconClock({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function IconActivity({ size = 16, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  );
}

function IconZap({ size = 16, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  );
}

function IconTrendingUp({ size = 16, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </svg>
  );
}

function IconArrowUp({ size = 12 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="19" x2="12" y2="5" />
      <polyline points="5 12 12 5 19 12" />
    </svg>
  );
}

function IconArrowDown({ size = 12 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <polyline points="19 12 12 19 5 12" />
    </svg>
  );
}

/* ====================================
   Compound Status Helper
   Derives informative status text from radio status + assignment
   ==================================== */
function getCompoundStatus(status: string, assignedTo: string): string {
  switch (status) {
    case 'online':
      return assignedTo !== 'Unassigned' ? 'Online / In Use' : 'Online / Available';
    case 'offline':
      return 'Offline';
    case 'stunned':
      return 'Stunned';
    case 'warning':
      return 'Warning';
    default:
      return status.charAt(0).toUpperCase() + status.slice(1);
  }
}

/* ====================================
   useCountUp Hook
   Smoothly animates a numeric value from 0 to its target.
   Uses requestAnimationFrame for buttery 60fps performance.
   ==================================== */
function useCountUp(target: number, duration: number = 800, enabled: boolean = true): number {
  const [current, setCurrent] = useState(0);
  const rafRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const previousTargetRef = useRef(0);

  useEffect(() => {
    if (!enabled || target === previousTargetRef.current) return;

    const startValue = previousTargetRef.current;
    previousTargetRef.current = target;
    startTimeRef.current = null;

    const animate = (timestamp: number) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;
      const elapsed = timestamp - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);

      // Ease-out cubic for a premium deceleration feel
      const eased = 1 - Math.pow(1 - progress, 3);
      const value = Math.round(startValue + (target - startValue) * eased);

      setCurrent(value);

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target, duration, enabled]);

  return enabled ? current : target;
}

/* ====================================
   StatsCard Component (Updated with trend indicators)
   ==================================== */
interface StatsCardProps {
  icon: ReactNode;
  accentColour: string;
  label: string;
  value: string | number;
  sublabel?: string;
  sublabelColour?: string;
  trend?: {
    direction: 'up' | 'down' | 'neutral';
    value: string;
  };
  animationDelay?: number;
}

function StatsCard({ icon, accentColour, label, value, sublabel, sublabelColour, trend, animationDelay = 0 }: StatsCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  // Determine background colour for icon based on accent
  const getIconBg = () => {
    switch (accentColour) {
      case colours.blue: return 'var(--blue-bg)';
      case colours.orange: return 'var(--orange-bg)';
      case colours.red: return 'var(--red-bg)';
      case colours.amber: return 'var(--amber-bg)';
      default: return 'var(--blue-bg)';
    }
  };

  return (
    <Box
      style={{
        padding: '20px 24px',
        background: 'var(--bg-card)',
        borderRadius: 12,
        borderTop: `3px solid ${accentColour}`,
        boxShadow: isHovered ? 'var(--shadow-hover)' : 'var(--shadow)',
        transition: 'all 200ms ease',
        cursor: 'default',
        animation: `slideInUp 400ms ease ${animationDelay}ms backwards`,
        transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Group justify="space-between" align="flex-start">
        {/* Content */}
        <Box>
          {/* Label */}
          <Text
            style={{
              fontSize: '0.75rem',
              fontWeight: 500,
              color: 'var(--text-muted)',
              marginBottom: 8,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            {label}
          </Text>

          {/* Value */}
          <Text
            style={{
              fontSize: '1.5rem',
              fontWeight: 600,
              color: 'var(--text-primary)',
              lineHeight: 1.2,
              marginBottom: (sublabel || trend) ? 6 : 0,
            }}
          >
            {value}
          </Text>

          {/* Trend indicator or sublabel */}
          {trend && (
            <Group gap={4} style={{ marginTop: 4 }}>
              <Box
                style={{
                  color: trend.direction === 'up' ? colours.green
                       : trend.direction === 'down' ? colours.red
                       : 'var(--text-muted)',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {trend.direction === 'up' && <IconArrowUp size={14} />}
                {trend.direction === 'down' && <IconArrowDown size={14} />}
              </Box>
              <Text
                style={{
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  color: trend.direction === 'up' ? colours.green
                       : trend.direction === 'down' ? colours.red
                       : 'var(--text-muted)',
                }}
              >
                {trend.value}
              </Text>
            </Group>
          )}

          {/* Sublabel (only show if no trend) */}
          {sublabel && !trend && (
            <Text
              style={{
                fontSize: '0.8125rem',
                color: sublabelColour || 'var(--text-secondary)',
                fontWeight: sublabelColour ? 500 : 400,
              }}
            >
              {sublabel}
            </Text>
          )}
        </Box>

        {/* Icon with coloured background - larger size */}
        <Box
          style={{
            width: 52,
            height: 52,
            borderRadius: 12,
            backgroundColor: getIconBg(),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: accentColour,
            flexShrink: 0,
            transition: 'transform 200ms ease',
            transform: isHovered ? 'scale(1.05)' : 'scale(1)',
          }}
        >
          {icon}
        </Box>
      </Group>
    </Box>
  );
}

/* ====================================
   StatusDot Component
   ==================================== */
function StatusDot({ status, size = 8 }: { status: 'online' | 'offline' | 'warning' | 'stunned'; size?: number }) {
  const statusColours = {
    online: colours.green,
    offline: colours.grey,
    warning: colours.amber,
    stunned: colours.red,
  };

  return (
    <Box
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: statusColours[status],
        flexShrink: 0,
      }}
    />
  );
}

/* ====================================
   FilterPill Component (Updated with lock icon)
   ==================================== */
function FilterPill({
  label,
  count,
  active,
  onClick,
  colour,
  icon,
}: {
  label: string;
  count?: number;
  active: boolean;
  onClick: () => void;
  colour?: string;
  icon?: ReactNode;
}) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <UnstyledButton
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        padding: '6px 12px',
        borderRadius: 20,
        backgroundColor: active ? 'var(--text-primary)' : isHovered ? 'var(--bg-base)' : 'transparent',
        border: active ? 'none' : '1px solid var(--border-colour)',
        transition: 'all 150ms ease',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
      }}
    >
      {icon && (
        <Box style={{ color: active ? 'var(--bg-card)' : colour || 'var(--text-muted)', display: 'flex' }}>
          {icon}
        </Box>
      )}
      {colour && !icon && (
        <Box
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: colour,
          }}
        />
      )}
      {label && (
        <Text
          style={{
            fontSize: '0.8125rem',
            fontWeight: 500,
            color: active ? 'var(--bg-card)' : 'var(--text-secondary)',
          }}
        >
          {label}
        </Text>
      )}
      {count !== undefined && (
        <Text
          style={{
            fontSize: '0.8125rem',
            fontWeight: 500,
            color: active ? 'var(--bg-card)' : 'var(--text-muted)',
          }}
        >
          {count}
        </Text>
      )}
    </UnstyledButton>
  );
}

/* ====================================
   SortableHeader Component
   ==================================== */
function SortableHeader({
  label,
  sortKey,
  currentSort,
  onSort,
}: {
  label: string;
  sortKey: string;
  currentSort: { key: string; direction: 'asc' | 'desc' } | null;
  onSort: (key: string) => void;
}) {
  const isActive = currentSort?.key === sortKey;
  const direction = isActive ? currentSort.direction : null;

  return (
    <UnstyledButton
      onClick={() => onSort(sortKey)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        color: isActive ? 'var(--text-primary)' : 'var(--text-muted)',
        transition: 'color 150ms ease',
        fontSize: '0.6875rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      }}
    >
      {label}
      <Box
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
          opacity: isActive ? 1 : 0.4,
        }}
      >
        <Box style={{ color: direction === 'asc' ? colours.blue : 'currentColor', lineHeight: 0.6 }}>
          <svg width="8" height="8" viewBox="0 0 24 24" fill="currentColor">
            <polygon points="12 2 22 14 2 14" />
          </svg>
        </Box>
        <Box style={{ color: direction === 'desc' ? colours.blue : 'currentColor', lineHeight: 0.6 }}>
          <svg width="8" height="8" viewBox="0 0 24 24" fill="currentColor">
            <polygon points="12 22 2 10 22 10" />
          </svg>
        </Box>
      </Box>
    </UnstyledButton>
  );
}

/* ====================================
   RadioFleetTable Component (Updated layout)
   ==================================== */
function RadioFleetTable({ animationDelay = 0 }: { animationDelay?: number }) {
  const [filter, setFilter] = useState<'all' | 'online' | 'warning' | 'stunned' | 'offline'>('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

  // Use real topology data from API
  const { data: topology, isLoading: topologyLoading } = useTopology();

  // Transform topology data to table format
  const radios = useMemo(() => {
    if (!topology?.radios) return [];
    return topology.radios.map((radio) => ({
      radioId: radio.dmrId,
      alias: radio.equipment?.description || `Radio ${radio.dmrId}`,
      status: radio.status as 'online' | 'offline' | 'warning' | 'stunned',
      location: topology.sites.find(s => s.id === radio.siteId)?.name || 'Unknown',
      assignedTo: radio.equipment?.tagId || 'Unassigned',
      lastSeen: radio.lastSeenAt ? formatLastSeen(radio.lastSeenAt) : 'Never',
    }));
  }, [topology]);

  // Filter and sort radios
  const filteredRadios = useMemo(() => {
    let result = radios.filter((radio) => {
      // Status filter
      if (filter !== 'all' && radio.status !== filter) return false;

      // Search filter
      if (search) {
        const searchLower = search.toLowerCase();
        return (
          radio.radioId.toLowerCase().includes(searchLower) ||
          radio.alias.toLowerCase().includes(searchLower) ||
          radio.assignedTo.toLowerCase().includes(searchLower)
        );
      }

      return true;
    });

    // Sort
    if (sort) {
      result = [...result].sort((a, b) => {
        const aVal = a[sort.key as keyof typeof a] || '';
        const bVal = b[sort.key as keyof typeof b] || '';
        const comparison = String(aVal).localeCompare(String(bVal));
        return sort.direction === 'asc' ? comparison : -comparison;
      });
    }

    return result;
  }, [radios, filter, search, sort]);

  // Count by status
  const counts = useMemo(() => ({
    online: radios.filter((r) => r.status === 'online').length,
    warning: radios.filter((r) => r.status === 'warning').length,
    stunned: radios.filter((r) => r.status === 'stunned').length,
    offline: radios.filter((r) => r.status === 'offline').length,
  }), [radios]);

  const handleSort = (key: string) => {
    if (sort?.key === key) {
      setSort({ key, direction: sort.direction === 'asc' ? 'desc' : 'asc' });
    } else {
      setSort({ key, direction: 'asc' });
    }
  };

  return (
    <Box
      style={{
        background: 'var(--bg-card)',
        borderRadius: 12,
        boxShadow: 'var(--shadow)',
        overflow: 'hidden',
        animation: `slideInUp 400ms ease ${animationDelay}ms backwards`,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header - Title and controls inline */}
      <Box
        style={{
          padding: '20px 24px',
          borderBottom: '1px solid var(--border-colour)',
        }}
      >
        <Group justify="space-between" align="center">
          {/* Left: Title and count */}
          <Group gap={12}>
            <Text
              style={{
                fontSize: '1rem',
                fontWeight: 600,
                color: 'var(--text-primary)',
              }}
            >
              Radio Fleet
            </Text>
            <Box
              style={{
                padding: '4px 10px',
                borderRadius: 12,
                backgroundColor: 'var(--bg-base)',
              }}
            >
              <Text
                style={{
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: 'var(--text-muted)',
                }}
              >
                {radios.length} units
              </Text>
            </Box>
          </Group>

          {/* Right: Search and filter pills inline */}
          <Group gap={12}>
            {/* Filter pills */}
            <Group gap={6}>
              <FilterPill label="All" active={filter === 'all'} onClick={() => setFilter('all')} />
              <FilterPill label="" count={counts.online} active={filter === 'online'} onClick={() => setFilter('online')} colour={colours.green} />
              <FilterPill label="" count={counts.warning} active={filter === 'warning'} onClick={() => setFilter('warning')} colour={colours.amber} />
              <FilterPill
                label=""
                count={counts.stunned}
                active={filter === 'stunned'}
                onClick={() => setFilter('stunned')}
                colour={colours.red}
                icon={<IconLock size={12} />}
              />
              <FilterPill label="" count={counts.offline} active={filter === 'offline'} onClick={() => setFilter('offline')} colour={colours.grey} />
            </Group>

            {/* Search input */}
            <TextInput
              placeholder="Search radios..."
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              leftSection={<IconSearch size={14} />}
              size="sm"
              styles={{
                input: {
                  backgroundColor: 'var(--bg-base)',
                  border: '1px solid var(--border-colour)',
                  color: 'var(--text-primary)',
                  fontSize: '0.875rem',
                  borderRadius: 8,
                  '&::placeholder': {
                    color: 'var(--text-muted)',
                  },
                  '&:focus': {
                    borderColor: colours.blue,
                  },
                },
              }}
              style={{ width: 180 }}
            />
          </Group>
        </Group>
      </Box>

      {/* Table */}
      <ScrollArea style={{ flex: 1 }}>
        <Table
          highlightOnHover
          styles={{
            table: {
              backgroundColor: 'transparent',
            },
            thead: {
              backgroundColor: 'var(--bg-base)',
            },
            th: {
              padding: '12px 16px',
              borderBottom: '1px solid var(--border-colour)',
            },
            td: {
              fontSize: '0.875rem',
              color: 'var(--text-primary)',
              padding: '14px 16px',
              borderBottom: '1px solid var(--border-colour)',
            },
            tr: {
              transition: 'background-color 150ms ease',
            },
          }}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th>
                <SortableHeader label="Radio ID" sortKey="radioId" currentSort={sort} onSort={handleSort} />
              </Table.Th>
              <Table.Th>
                <SortableHeader label="Alias" sortKey="alias" currentSort={sort} onSort={handleSort} />
              </Table.Th>
              <Table.Th>
                <SortableHeader label="Status" sortKey="status" currentSort={sort} onSort={handleSort} />
              </Table.Th>
              <Table.Th>
                <SortableHeader label="Location" sortKey="location" currentSort={sort} onSort={handleSort} />
              </Table.Th>
              <Table.Th>
                <SortableHeader label="Assigned To" sortKey="assignedTo" currentSort={sort} onSort={handleSort} />
              </Table.Th>
              <Table.Th>
                <SortableHeader label="Last Seen" sortKey="lastSeen" currentSort={sort} onSort={handleSort} />
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {topologyLoading ? (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Text ta="center" py="xl" size="sm" c="dimmed">Loading radio data...</Text>
                </Table.Td>
              </Table.Tr>
            ) : filteredRadios.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Text ta="center" py="xl" size="sm" c="dimmed">
                    {radios.length === 0 ? 'No radios found. Connect to the radio gateway to see fleet data.' : 'No radios match the current filter.'}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              filteredRadios.map((radio, rowIndex) => (
                <Table.Tr
                  key={radio.radioId}
                  style={{
                    /* Premium: staggered row entrance with subtle slide-up and fade-in */
                    animation: `tableRowSlideIn 350ms ease-out ${rowIndex * 40}ms backwards`,
                  }}
                >
                  <Table.Td>
                    <Group gap={8}>
                      <Box style={{ color: colours.blue }}>
                        <IconRadio size={16} />
                      </Box>
                      <Text
                        style={{
                          fontWeight: 500,
                          color: colours.blue,
                          cursor: 'pointer',
                          transition: 'opacity 150ms ease',
                        }}
                      >
                        {radio.radioId}
                      </Text>
                    </Group>
                  </Table.Td>
                  <Table.Td style={{ fontWeight: 500 }}>{radio.alias}</Table.Td>
                  <Table.Td>
                    <Group gap={8}>
                      <StatusDot status={radio.status} />
                      <Text style={{
                        fontSize: '0.875rem',
                        color: radio.status === 'online' ? colours.green
                             : radio.status === 'warning' ? colours.amber
                             : radio.status === 'stunned' ? colours.red
                             : 'var(--text-muted)',
                        fontWeight: 500,
                      }}>
                        {/* Compound status: shows availability alongside connection state */}
                        {getCompoundStatus(radio.status, radio.assignedTo)}
                      </Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={6}>
                      <Box style={{ color: 'var(--text-muted)' }}>
                        <IconMapPin size={14} />
                      </Box>
                      <Text style={{ color: 'var(--text-secondary)' }}>{radio.location}</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={6}>
                      <Box style={{ color: 'var(--text-muted)' }}>
                        <IconUser size={14} />
                      </Box>
                      <Text style={{ color: radio.assignedTo === 'Unassigned' ? 'var(--text-muted)' : 'var(--text-secondary)' }}>
                        {radio.assignedTo}
                      </Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={6}>
                      <Box style={{ color: 'var(--text-muted)' }}>
                        <IconClock size={14} />
                      </Box>
                      <Text style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
                        {radio.lastSeen}
                      </Text>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </Box>
  );
}

/* ====================================
   MiniBarChart Component - Shows activity when data available
   ==================================== */
function MiniBarChart({ hasData }: { hasData: boolean }) {
  if (!hasData) {
    return (
      <Box
        style={{
          height: 56,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--bg-base)',
          borderRadius: 4,
        }}
      >
        <Text style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
          No activity data available
        </Text>
      </Box>
    );
  }

  // When we have real data, this would show actual activity
  // For now, show empty placeholder
  return (
    <Box
      style={{
        height: 56,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--bg-base)',
        borderRadius: 4,
      }}
    >
      <Text style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
        Activity data will appear here
      </Text>
    </Box>
  );
}

/* ====================================
   FleetStatusBar Component (Updated - horizontal with counts)
   ==================================== */
function FleetStatusBar({ online, warning, stunned, offline }: { online: number; warning: number; stunned: number; offline: number }) {
  const total = online + warning + stunned + offline;
  if (total === 0) return null;

  const segments = [
    { value: online, colour: colours.green, label: 'Online' },
    { value: warning, colour: colours.amber, label: 'Warning' },
    { value: stunned, colour: colours.red, label: 'Stunned' },
    { value: offline, colour: colours.grey, label: 'Offline' },
  ];

  return (
    <Box>
      {/* Progress bar */}
      <Box
        style={{
          display: 'flex',
          height: 8,
          borderRadius: 4,
          overflow: 'hidden',
          backgroundColor: 'var(--border-colour)',
        }}
      >
        {segments.map((segment, index) => (
          segment.value > 0 && (
            <Box
              key={index}
              style={{
                width: `${(segment.value / total) * 100}%`,
                backgroundColor: segment.colour,
                transition: 'width 300ms ease',
              }}
            />
          )
        ))}
      </Box>

      {/* Horizontal legend with counts */}
      <Group gap={16} mt={12} wrap="wrap">
        {segments.map((segment, index) => (
          segment.value > 0 && (
            <Group key={index} gap={6}>
              <Box
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 2,
                  backgroundColor: segment.colour,
                }}
              />
              <Text style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                {segment.label}
              </Text>
              <Text style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
                {segment.value}
              </Text>
            </Group>
          )
        ))}
      </Group>
    </Box>
  );
}

/* ====================================
   FleetOverview Panel Component (Updated styling)
   ==================================== */
function FleetOverviewPanel({ animationDelay = 0 }: { animationDelay?: number }) {
  const [statusBoxHovered, setStatusBoxHovered] = useState(false);

  // Use real data from API
  const { data: topology } = useTopology();
  const { data: sessions } = useActiveSessions();

  // Calculate metrics from real API data only
  const totalRadios = topology?.radios?.length ?? 0;
  const onlineRadios = topology?.radios?.filter((r) => r.status === 'online').length ?? 0;
  const warningRadios = topology?.radios?.filter((r) => (r.status as string) === 'warning').length ?? 0;
  const stunnedRadios = topology?.radios?.filter((r) => r.status === 'stunned').length ?? 0;
  const offlineRadios = topology?.radios?.filter((r) => r.status === 'offline').length ?? 0;

  const percentage = totalRadios > 0 ? Math.round((onlineRadios / totalRadios) * 100) : 0;
  const activeSessions = sessions?.items?.length ?? 0;
  const utilisation = totalRadios > 0 ? Math.round((activeSessions / totalRadios) * 100) : 0;

  // Determine status
  const status = totalRadios === 0 ? 'nodata' : percentage >= 80 ? 'healthy' : percentage >= 50 ? 'degraded' : 'critical';
  const statusConfig = {
    healthy: {
      colour: colours.green,
      label: 'Healthy',
      icon: <IconActivity size={16} />,
      bgColour: 'var(--green-bg)',
    },
    degraded: {
      colour: colours.amber,
      label: 'Degraded',
      icon: <IconAlertTriangle size={16} />,
      bgColour: 'var(--amber-bg)',
    },
    critical: {
      colour: colours.red,
      label: 'Critical',
      icon: <IconAlertTriangle size={16} />,
      bgColour: 'var(--red-bg)',
    },
    nodata: {
      colour: colours.grey,
      label: 'No Data',
      icon: <IconActivity size={16} />,
      bgColour: 'var(--bg-base)',
    },
  };

  return (
    <Box
      style={{
        background: 'var(--bg-card)',
        borderRadius: 12,
        boxShadow: 'var(--shadow)',
        padding: '20px',
        animation: `slideInUp 400ms ease ${animationDelay}ms backwards`,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Group justify="space-between" mb={20}>
        <Text
          style={{
            fontSize: '1rem',
            fontWeight: 600,
            color: 'var(--text-primary)',
          }}
        >
          Fleet Overview
        </Text>
        {/* Premium: Live indicator with expanding pulse ring */}
        <Group gap={6}>
          <Box
            style={{
              position: 'relative',
              width: 14,
              height: 14,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* Expanding pulse ring - radiates outward from the dot */}
            <Box
              style={{
                position: 'absolute',
                width: 8,
                height: 8,
                borderRadius: '50%',
                border: `1.5px solid ${colours.green}`,
                animation: 'pulseRing 2s ease-out infinite',
              }}
            />
            {/* Core dot with gentle opacity pulse */}
            <Box
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: colours.green,
                animation: 'liveDotPulse 2s ease-in-out infinite',
                position: 'relative',
                zIndex: 1,
              }}
            />
          </Box>
          <Text
            style={{
              fontSize: '0.75rem',
              fontWeight: 500,
              color: colours.green,
            }}
          >
            Live
          </Text>
        </Group>
      </Group>

      {/* Status Box - Coloured background with premium shimmer on hover */}
      <Box
        onMouseEnter={() => setStatusBoxHovered(true)}
        onMouseLeave={() => setStatusBoxHovered(false)}
        style={{
          padding: '16px',
          borderRadius: 8,
          backgroundColor: statusConfig[status].bgColour,
          marginBottom: 20,
          position: 'relative',
          overflow: 'hidden',
          transition: 'box-shadow 250ms ease',
          boxShadow: statusBoxHovered ? `0 0 0 1px ${statusConfig[status].colour}20` : 'none',
          /* Premium: shimmer shine overlay effect on hover */
          backgroundImage: statusBoxHovered
            ? `linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.15) 50%, transparent 70%)`
            : 'none',
          backgroundSize: '200% 100%',
          animation: statusBoxHovered ? 'shimmer 1.2s ease-in-out' : 'none',
        }}
      >
        <Group justify="space-between" align="flex-start">
          {/* Left side - Status info */}
          <Box>
            <Group gap={8} mb={6}>
              <Box style={{ color: statusConfig[status].colour }}>
                {statusConfig[status].icon}
              </Box>
              <Text
                style={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: statusConfig[status].colour,
                }}
              >
                {statusConfig[status].label}
              </Text>
            </Group>
            <Text style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
              {onlineRadios} of {totalRadios} radios online
            </Text>
          </Box>

          {/* Right side - Percentage */}
          <Text style={{ fontSize: '2rem', fontWeight: 700, color: statusConfig[status].colour, lineHeight: 1 }}>
            {percentage}%
          </Text>
        </Group>

        {/* Progress bar */}
        <Box
          style={{
            height: 6,
            borderRadius: 3,
            backgroundColor: 'rgba(0,0,0,0.1)',
            marginTop: 12,
            overflow: 'hidden',
          }}
        >
          <Box
            style={{
              height: '100%',
              width: `${percentage}%`,
              backgroundColor: statusConfig[status].colour,
              borderRadius: 3,
              transition: 'width 500ms ease',
            }}
          />
        </Box>
      </Box>

      {/* Metrics row */}
      <Group justify="space-between" mb={24} style={{ padding: '16px 0', borderTop: '1px solid var(--border-colour)', borderBottom: '1px solid var(--border-colour)' }}>
        <Box style={{ textAlign: 'center', flex: 1 }}>
          <Group gap={4} justify="center" mb={4}>
            <IconActivity size={14} style={{ color: 'var(--text-muted)' }} />
            <Text style={{ fontSize: '0.6875rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>
              Utilisation
            </Text>
          </Group>
          <Text style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--text-primary)' }}>
            {utilisation}%
          </Text>
        </Box>
        <Box style={{ width: 1, height: 40, backgroundColor: 'var(--border-colour)' }} />
        <Box style={{ textAlign: 'center', flex: 1 }}>
          <Group gap={4} justify="center" mb={4}>
            <IconZap size={14} style={{ color: 'var(--text-muted)' }} />
            <Text style={{ fontSize: '0.6875rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>
              Active
            </Text>
          </Group>
          <Text style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--text-primary)' }}>
            {activeSessions}
          </Text>
        </Box>
        <Box style={{ width: 1, height: 40, backgroundColor: 'var(--border-colour)' }} />
        <Box style={{ textAlign: 'center', flex: 1 }}>
          <Group gap={4} justify="center" mb={4}>
            <IconTrendingUp size={14} style={{ color: 'var(--text-muted)' }} />
            <Text style={{ fontSize: '0.6875rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>
              Avg
            </Text>
          </Group>
          <Text style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--text-primary)' }}>
            {activeSessions > 0 ? '—' : '—'}
          </Text>
        </Box>
      </Group>

      {/* 12H Activity Chart */}
      <Box mb={24}>
        <Text
          style={{
            fontSize: '0.6875rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: 'var(--text-muted)',
            marginBottom: 12,
          }}
        >
          12H Activity
        </Text>
        <MiniBarChart hasData={activeSessions > 0} />
      </Box>

      {/* Fleet Status */}
      <Box style={{ marginTop: 'auto' }}>
        <Text
          style={{
            fontSize: '0.6875rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: 'var(--text-muted)',
            marginBottom: 12,
          }}
        >
          Fleet Status
        </Text>
        <FleetStatusBar
          online={onlineRadios}
          warning={warningRadios}
          stunned={stunnedRadios}
          offline={offlineRadios}
        />
      </Box>
    </Box>
  );
}

/* ====================================
   Helper Functions
   ==================================== */
function formatLastSeen(isoDate: string): string {
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
}

/* ====================================
   Main Dashboard Component
   ==================================== */
export function DashboardPage() {
  const { data: sessions, isLoading: loadingSessions } = useActiveSessions();
  const { data: topology, isLoading: loadingTopology } = useTopology();

  // Computed metrics
  const activeSessionCount = sessions?.items.length ?? 0;
  const overdueCount = sessions?.items.filter((s) => s.isOverdue).length ?? 0;
  const stunnedRadios = topology?.radios.filter((r) => r.status === 'stunned').length ?? 0;

  // Infrastructure counts
  const siteCount = topology?.sites.length ?? 0;
  const repeaterCount = topology?.sites.reduce((acc, site) => acc + site.repeaters.length, 0) ?? 0;

  const isLoading = loadingSessions || loadingTopology;

  // Premium: smooth count-up animation for stats card values
  const animatedSessionCount = useCountUp(activeSessionCount, 700, !isLoading);
  const animatedOverdueCount = useCountUp(overdueCount, 700, !isLoading);
  const animatedStunnedCount = useCountUp(stunnedRadios, 700, !isLoading);
  const animatedSiteCount = useCountUp(siteCount, 700, !isLoading);
  const animatedRepeaterCount = useCountUp(repeaterCount, 700, !isLoading);

  return (
    <Stack gap={24}>
      {/* ================================
          Stats Cards Row (Updated with trend indicators and count-up animation)
          ================================ */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing={16}>
        <StatsCard
          icon={<IconUsers size={28} />}
          accentColour={colours.blue}
          label="Active Sessions"
          value={isLoading ? '...' : `${animatedSessionCount} users`}
          {...(!isLoading ? { trend: { direction: 'up' as const, value: `+${Math.min(activeSessionCount, 3)} today` } } : {})}
          animationDelay={100}
        />
        <StatsCard
          icon={<IconBuilding size={28} />}
          accentColour={colours.orange}
          label="Infrastructure"
          value={isLoading ? '...' : `${animatedSiteCount} sites / ${animatedRepeaterCount} repeaters`}
          {...(!isLoading ? { trend: { direction: 'neutral' as const, value: 'Stable' } } : {})}
          animationDelay={150}
        />
        <StatsCard
          icon={<IconAlertTriangle size={28} />}
          accentColour={colours.red}
          label="Overdue"
          value={isLoading ? '...' : `${animatedOverdueCount} radio${overdueCount !== 1 ? 's' : ''}`}
          trend={overdueCount > 0 ? { direction: 'up', value: 'Requires attention' } : { direction: 'down', value: 'All on time' }}
          animationDelay={200}
        />
        <StatsCard
          icon={<IconLock size={28} />}
          accentColour={colours.amber}
          label="Stunned"
          value={isLoading ? '...' : `${animatedStunnedCount} radio${stunnedRadios !== 1 ? 's' : ''}`}
          sublabel="Currently disabled"
          animationDelay={250}
        />
      </SimpleGrid>

      {/* ================================
          Main Content - Two Column Layout
          ================================ */}
      <Box
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 340px',
          gap: 16,
          minHeight: 520,
        }}
      >
        {/* Left Column - Radio Fleet Table */}
        <RadioFleetTable animationDelay={300} />

        {/* Right Column - Fleet Overview Panel */}
        <FleetOverviewPanel animationDelay={350} />
      </Box>
    </Stack>
  );
}
